import React, { memo, useCallback } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button, Icon } from "@components/index";
import type { IIntegrationItem } from "@utils/types/integration";
import type { ButtonVariant } from "@components/Button";
import "./style.css";

interface DialogProps {
    open: boolean;
    item: IIntegrationItem | null;
    title?: string;
    description?: (name: string) => React.ReactNode;
    iconName?: string;
    iconColorClass?: string;
    iconWrapperClass?: string;
    cancelLabel?: string;
    confirmLabel?: string;
    cancelVariant?: ButtonVariant;
    confirmVariant?: ButtonVariant;
    isCancelDisabled?: boolean;
    isConfirmDisabled?: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const DEFAULT_DESCRIPTION = (name: string) => (
    <>
        Are you sure you want to proceed with{" "}
        <strong>{name}</strong>? This action cannot be undone.
    </>
);

const AppDialog: React.FC<DialogProps> = ({
    open,
    item,
    title = "Warning",
    description = DEFAULT_DESCRIPTION,
    iconName = "info",
    iconColorClass = "fill-red-500",
    iconWrapperClass = "bg-red-100",
    cancelLabel = "Cancel",
    confirmLabel = "Confirm",
    cancelVariant = "default",
    confirmVariant = "error",
    isCancelDisabled = false,
    isConfirmDisabled = false,
    onClose,
    onConfirm,
}) => {
    const handleClose = useCallback(onClose, [onClose]);
    const handleConfirm = useCallback(onConfirm, [onConfirm]);

    if (!open || !item) return null;

    return (
        <Dialog onClose={handleClose} className="custom-dialog">
            <div>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${iconWrapperClass}`}>
                    <Icon
                        name={iconName}
                        width={32}
                        height={32}
                        className={iconColorClass}
                    />
                </div>

                <h2 className="text-[20px] font-bold mb-2">
                    {title}
                </h2>

                <p className="text-[14px] text-gray-600 max-w-[420px] mb-6 text-center leading-5">
                    {description(item.name)}
                </p>

                <div className="mt-auto flex gap-3 pt-3">
                    <Button
                        label={cancelLabel}
                        variant={cancelVariant}
                        size="small"
                        onClick={handleClose}
                        disabled={isCancelDisabled}
                        className="!w-[255px] !h-[32px] outline-btn !text-[14px] !capitalize"
                    />
                    <Button
                        label={confirmLabel}
                        variant={confirmVariant}
                        size="small"
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                        className="!w-[255px] !h-[32px] !text-[14px] !capitalize"
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default memo(AppDialog);